import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Box,
  useTheme,
  Button,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  AssignmentReturn,
  CardMembership,
  ExpandLess,
  ExpandMore,
  School,
} from "@mui/icons-material";
import moment from "moment";
import courseSchema from "./courseSchema";
import {
  createTrainingCourse,
  updateTrainingCourse,
} from "../../../reducers/TrainingCourseReducer";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import FormSection from "../../../components/Form/FormSection";
import {
  courseCertificationFields,
  courseInformationFields,
} from "./courseFieldConfigs";
import { useCourseData } from "./useCourseData";
import CourseParticipantTable from "./CourseParticipantTable";
import { getParticipantsByCourseNumber } from "../../../reducers/TrainingParticipantReducer";
import { notify } from "../../../reducers/NotifierReducer";
import { createMultipleTrainingCertifications } from "../../../reducers/TrainingCertificationReducer";
import {
  PATH_TRAINING_COURSES,
  PATH_TRAINING_COURSES_EDIT,
} from "../../../utils/routeConstants";
import TagsTable from "../../../components/Tags/TagsTable";
import { PerTagType } from "../../../utils/enums";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import { entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import { getService } from "../../../reducers/service";

const AddEditTrainingCourse = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const readOnly = queryParams.get("readOnly") === "true";

  useEffect(() => {
    if (location.search && queryParams.get("readOnly") !== "true") {
      dispatch(notify("The requested page does not exist.", "warning"));
      history.push(PATH_TRAINING_COURSES);
    }
  }, [id, location.search, history]);

  const [allExpanded, setAllExpanded] = useState(true);

  const handleToggleAll = () => {
    setAllExpanded((prev) => !prev);
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(courseSchema),
    defaultValues: {
      id: null,
      courseName: "",
      courseBeginDate: "",
      courseEndDate: "",
      location: "",
      hours: "",
      category: "",
      approved: false,
      courseNumber: "",
      instructor: "",
      certification: "",
      expireDate: "",
      certificationEntered: false,
      tuitionDefault: "",
    },
  });

  const { loaded, locationOptions, categoryOptions, tagTypeOptions } =
    useCourseData(id, reset);

  const { participantsByCourse } = useSelector(
    (state) => state.trainingParticipants
  );

  const userAgency = useSelector((state) => state.user.userAgency);

  const personnelTags = useSelector((state) =>
    state.personnelTags.personnelTags &&
    state.personnelTags.personnelTags[PerTagType.Course]
      ? state.personnelTags.personnelTags[PerTagType.Course][id]
      : []
  );

  const courseParticipants =
    participantsByCourse &&
    participantsByCourse[control._formValues.courseNumber]
      ? participantsByCourse[control._formValues.courseNumber]
      : [];

  const courseInfoTabPermission = getAccessPermission(
    "training",
    "Course Information",
    "any"
  );
  const courseInfoTabReadPermission = courseInfoTabPermission?.Read ?? true;
  const courseInfoTabEditPermission = courseInfoTabPermission?.Edit ?? true;

  const courseParticipantTabPermission = getAccessPermission(
    "training",
    "Course Participant",
    "any"
  );
  const courseParticipantTabReadPermission =
    courseParticipantTabPermission?.Read ?? true;
  const courseParticipantTabAddPermission =
    courseParticipantTabPermission?.Create ?? true;
  const courseParticipantTabEditPermission =
    courseParticipantTabPermission?.Edit ?? true;
  const courseParticipantTabDeletePermission =
    courseParticipantTabPermission?.Delete ?? true;

  const courseCertificateTabPermission = getAccessPermission(
    "training",
    "Course Certificate Information",
    "any"
  );
  const courseCertificateTabReadPermission =
    courseCertificateTabPermission?.Read ?? true;
  const courseCertificateTabEditPermission =
    courseCertificateTabPermission?.Edit ?? true;

  const courseTagsTabPermission = getAccessPermission(
    "training",
    "Course Tags",
    "any"
  );
  const courseTagsTabReadPermission = courseTagsTabPermission?.Read ?? true;

  useEffect(() => {
    if (id && control._formValues.courseNumber) {
      dispatch(getParticipantsByCourseNumber(control._formValues.courseNumber));
    }
  }, [control._formValues.courseNumber, dispatch, id]);

  const options = {
    location: locationOptions,
    category: categoryOptions,
  };

  useEffect(() => {
    setValue("courseNumber", "");
  }, [userAgency]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.CourseId,
      agencyID: userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    if (id?.includes("Agency ID does not match")) {
      dispatch(
        notify(
          "Unable to generate Course ID because the Agency ID does not match.",
          "warning"
        )
      );
      return null;
    }
    return id;
  };

  const handleAutoGenerateClick = async () => {
    const id = await generateCustomId();
    setValue("courseNumber", id);
  };

  const onSubmit = async (data) => {
    const courseId = await generateCustomId();
    const cleanedData = {
      courseName: data.courseName.trim() || null,
      courseBeginDate: data.courseBeginDate
        ? moment(data.courseBeginDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      courseEndDate: data.courseEndDate
        ? moment(data.courseEndDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      location: data.location.trim() || null,
      hours:
        data.hours !== "" && !isNaN(Number(data.hours))
          ? Number(data.hours)
          : null,
      category: data.category.trim() || null,
      courseNumber: data.courseNumber.trim() || courseId,
      instructor: data.instructor.trim() || null,
      certification: data.certification.trim() || null,
      expireDate: data.expireDate
        ? moment(data.expireDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      certificationEntered: data.certificationEntered || null,
      tuitionDefault:
        data.tuitionDefault === "" || data.tuitionDefault == null
          ? null
          : data.tuitionDefault,

      agencyId: userAgency,
    };

    if (id) {
      await dispatch(updateTrainingCourse(data.id, cleanedData));
      history.push(`${PATH_TRAINING_COURSES}`);
    } else {
      dispatch(createTrainingCourse(cleanedData)).then((result) => {
        if (result?.duplicate) {
          dispatch(
            notify(
              `Course Number "${cleanedData.courseNumber}" already exists.`,
              "warning"
            )
          );
          return;
        }

        if (result?.id) {
          history.push(`${PATH_TRAINING_COURSES_EDIT}${result.id}`);
        } else {
          history.push(`${PATH_TRAINING_COURSES}`);
        }
      });
    }
  };

  const handleCertificationEntered = async () => {
    if (courseParticipants.length === 0) {
      dispatch(notify("No participants in this course", "warning"));
      return;
    }

    const certificationsToCreate = courseParticipants.map((p) => {
      return {
        certificationType: control._formValues.certification,
        dateObtained: new Date(),
        expireDate: control._formValues.expireDate,
        employee: p.employeeNum,
      };
    });
    const successCount = await dispatch(
      createMultipleTrainingCertifications(certificationsToCreate)
    );
    if (successCount === certificationsToCreate.length) {
      await dispatch(
        updateTrainingCourse(id, {
          ...control._formValues,
          certificationEntered: true,
        })
      );
      history.push(`${PATH_TRAINING_COURSES}`);
    }
  };

  if (!loaded) return null;

  const sectionProps = {
    control,
    errors,
    options,
    theme,
    watch,
  };

  const renderIdGenerationButton = () => {
    return (
      <Box>
        <Tooltip title="Auto generate course number">
          <IconButton
            color="primary"
            aria-label="Auto generate course number"
            size="large"
            onClick={handleAutoGenerateClick}
          >
            <AssignmentReturn />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const formSection1 = [
    courseInfoTabReadPermission && {
      title: "Course Information",
      icon: <School />,
      fields: courseInformationFields,
      allExpanded,
      defaultExpanded: true,
      editPermission: id ? !readOnly && courseInfoTabEditPermission : true,
      isEdit: id ? true : false,
      renderIdGenerationButton: renderIdGenerationButton,
    },
  ];

  const formSection2 = [
    courseCertificateTabReadPermission && {
      title: "Certificate Information",
      icon: <CardMembership />,
      fields: courseCertificationFields,
      allExpanded,
      defaultExpanded: true,
      editPermission: id
        ? !readOnly && courseCertificateTabEditPermission
        : true,
    },
  ];

  return (
    loaded && (
      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
          margin: theme.spacing(1),
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          height: "calc(100vh - 100px)",
          marginTop: theme.spacing(0.5),
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleToggleAll}
              title={allExpanded ? "Collapse All" : "Expand All"}
            >
              {allExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Typography variant="h4" gutterBottom>
              {id ? "Edit Course" : "Add Course"}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              disabled={watch("certificationEntered")}
              onClick={() => handleCertificationEntered()}
            >
              Enter Certification
            </Button>
            {!readOnly && (
              <PATSaveButton
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
              />
            )}
            <PATCloseButton
              tooltipText="Close"
              onClick={() => history.push(`${PATH_TRAINING_COURSES}`)}
            />
          </Box>
        </Box>

        <Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
          <form>
            {formSection1.filter(Boolean).map((section) => (
              <FormSection key={section.title} {...section} {...sectionProps} />
            ))}
            {id ? (
              courseParticipantTabReadPermission && (
                <CourseParticipantTable
                  id={id}
                  theme={theme}
                  courseNumber={control._formValues.courseNumber}
                  courseParticipants={courseParticipants}
                  allExpanded={allExpanded}
                  canAdd={!readOnly && courseParticipantTabAddPermission}
                  canEdit={!readOnly && courseParticipantTabEditPermission}
                  canDelete={!readOnly && courseParticipantTabDeletePermission}
                />
              )
            ) : (
              <></>
            )}
            {formSection2.filter(Boolean).map((section) => (
              <FormSection key={section.title} {...section} {...sectionProps} />
            ))}
            {id ? (
              courseTagsTabReadPermission && (
                <TagsTable
                  id={id}
                  tagTypeOptions={tagTypeOptions}
                  personnelTags={personnelTags}
                  theme={theme}
                  allExpanded={allExpanded}
                  perTagType={PerTagType.Course}
                  readOnly={readOnly}
                />
              )
            ) : (
              <></>
            )}
          </form>
        </Box>
      </Paper>
    )
  );
};

export default AddEditTrainingCourse;
